
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { PlanId } from '@/config/pricing';

interface Subscription {
  tier: 'free' | 'premium' | 'pending_payment' | 'starter' | 'resume_pro' | 'ultimate';
  sessionsRemaining: number;
  resumeSessionsRemaining: number;
  resumeMonthlyUsed: number;
  expiresAt: string | null;
  planId: PlanId;
}

interface SubscriptionContextType {
  subscription: Subscription | null;
  isLoading: boolean;
  error: Error | null;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSubscription = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Return early with default subscription if no user
      if (!user) {
        setSubscription({
          tier: 'free',
          sessionsRemaining: 0,
          resumeSessionsRemaining: 0,
          resumeMonthlyUsed: 0,
          expiresAt: null,
          planId: 'free'
        });
        return;
      }

      // Check for existing subscription
      const { data: existingSubscription, error: fetchError } = await supabase
        .from('subscriptions')
        .select('tier, interview_sessions_remaining, resume_sessions_remaining, resume_sessions_monthly_used, expires_at, plan_id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (fetchError) {
        console.error('Error fetching subscription:', fetchError);
        throw new Error('Failed to fetch subscription');
      }

      if (existingSubscription) {
        // Edge case recovery: If institutional user has 0 sessions, fix it
        let sessionsRemaining = existingSubscription.interview_sessions_remaining || 0;
        
        if (sessionsRemaining === 0 && existingSubscription.tier === 'free') {
          // Check if user is institutional
          const { data: profile } = await supabase
            .from('profiles')
            .select('institution_id')
            .eq('id', user.id)
            .single();
          
          if (profile?.institution_id) {
            // User is institutional but has 0 sessions - fix it
            const { error: updateError } = await supabase
              .from('subscriptions')
              .update({ interview_sessions_remaining: 1 })
              .eq('user_id', user.id);
            
            if (!updateError) {
              sessionsRemaining = 1;
              console.log('Recovered institutional user session allocation');
            }
          }
        }
        
        // Map tier to planId - treat 'premium' as 'ultimate' for backward compatibility
        const planId = existingSubscription.plan_id as PlanId || 
          (existingSubscription.tier === 'premium' ? 'ultimate' : existingSubscription.tier as PlanId);
        
        setSubscription({
          tier: existingSubscription.tier,
          sessionsRemaining: sessionsRemaining,
          resumeSessionsRemaining: existingSubscription.resume_sessions_remaining || 0,
          resumeMonthlyUsed: existingSubscription.resume_sessions_monthly_used || 0,
          expiresAt: existingSubscription.expires_at,
          planId: planId
        });
        return;
      }

      // Check if user signed up through an institution
      const { data: profile } = await supabase
        .from('profiles')
        .select('institution_id')
        .eq('id', user.id)
        .single();

      const isInstitutionUser = !!profile?.institution_id;

      // Create new subscription if none exists
      // Institution users get 1 free interview, normal users get 0
      const { data: newSubscription, error: createError } = await supabase
        .from('subscriptions')
        .insert([
          {
            user_id: user.id,
            tier: 'free',
            interview_sessions_remaining: isInstitutionUser ? 1 : 0,
            resume_sessions_remaining: 1,
            plan_id: 'free'
          }
        ])
        .select('tier, interview_sessions_remaining, resume_sessions_remaining, resume_sessions_monthly_used, expires_at, plan_id')
        .limit(1)
        .single();

      if (createError) {
        console.error('Error creating subscription:', createError);
        throw new Error('Failed to create subscription');
      }

      if (!newSubscription) {
        throw new Error('No subscription data returned');
      }

      setSubscription({
        tier: newSubscription.tier,
        sessionsRemaining: newSubscription.interview_sessions_remaining || 0,
        resumeSessionsRemaining: newSubscription.resume_sessions_remaining || 0,
        resumeMonthlyUsed: newSubscription.resume_sessions_monthly_used || 0,
        expiresAt: newSubscription.expires_at,
        planId: (newSubscription.plan_id as PlanId) || 'free'
      });

    } catch (err) {
      console.error('Subscription error:', err);
      
      // Set fallback free subscription
      setSubscription({
        tier: 'free',
        sessionsRemaining: 0,
        resumeSessionsRemaining: 0,
        resumeMonthlyUsed: 0,
        expiresAt: null,
        planId: 'free'
      });
      
      // Only show error if we have a logged-in user
      if (user) {
        setError(err instanceof Error ? err : new Error('Failed to fetch subscription'));
        toast.error('Failed to load subscription status. Please try refreshing the page.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initializeSubscription = async () => {
      if (mounted) {
        await fetchSubscription();
      }
    };

    initializeSubscription();

    return () => {
      mounted = false;
    };
  }, [user?.id]);

  const contextValue: SubscriptionContextType = {
    subscription,
    isLoading,
    error,
    refreshSubscription: fetchSubscription
  };

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription(): SubscriptionContextType {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
